import { map } from "lodash";
import { AxiosResponse } from "axios";
import { axiosRequest } from "core/axios";
import { getHeaders } from "core/common-methods";
import { arrayofIds, checkUserPermissionWithOrgsOwnerShip } from "utils/common-methods";
import { OrganizationDTO } from "core/orgs/models";
import { PageDTO } from "core/utils/BasicModels";
import { PropertyDTO } from "core/property/models";

export const getRolesByAccountsIds = async (accountsIds: string[]) => {
  const roles = await axiosRequest.post<any, AxiosResponse>(
    `v2/accounts/ids/roles`,
    { ids: accountsIds },
    { headers: getHeaders().common }
  ).catch((r) => r);
  if (roles.status == 200 && roles.data) {
    return roles.data.content;
  }
  return [];
}

const getWorkspacesOrgsAccounts = async (workspaceId: string, params: any) => {
  const data = await axiosRequest.get<any, AxiosResponse>(
    `v2/workspaces/${workspaceId}/orgs/accounts?size=${params.size}`,
    { headers: getHeaders().common }
  )
    .catch((r) => r);
  if (data.status == 200) {
    const rolesByAccountsIds = await getRolesByAccountsIds(map(data.data.content, 'id'));
    return {
      members: data.data ?? [],
      roles: rolesByAccountsIds,
      status: true
    };
  }
  return {
    members: [],
    roles: [],
    status: false
  };
};

const getAccountsByOrgsIds = async (orgsIds: string[], params: any) => {
  const data = await axiosRequest.post<any, AxiosResponse>(
    `v2/orgs/ids/accounts?size=${params.size}`,
    { ids: orgsIds },
    { headers: getHeaders().common }
  )
    .catch((r) => r);
  if (data.status == 200) {
    const rolesByAccountsIds = await getRolesByAccountsIds(map(data.data.content, 'id'));
    return {
      members: data.data ?? [],
      roles: rolesByAccountsIds,
      status: true
    };
  }
  return {
    members: [],
    roles: [],
    status: false
  };
};

export const getWorkspaceAccounts = async (workspaceId: string, orgsIds:any, params = {size:9999}) => {
  const userHasLegacyOrRegularAccess = checkUserPermissionWithOrgsOwnerShip();
  if (userHasLegacyOrRegularAccess) {
    return getAccountsByOrgsIds(orgsIds, params);
  }
  return getWorkspacesOrgsAccounts(workspaceId, params);
};

export const getWorkspaceInvites = async (workspaceId: string) => {
  return await axiosRequest.get(
    `v2/workspaces/${workspaceId}/invite-status?size=9999&page=0&attributes=licensing_accounts`,
    { headers: getHeaders().common }
  )
  .then((r: any) => r.data.content)
  .catch();
}

export const resendInvite = async (inviteId: string) => {
  return await axiosRequest.post(
    `v2/accounts/invitation/${inviteId}/resend`, {}, 
    { headers: getHeaders().common }
  )
}

export const revokeInvite = async (inviteId: string) => {
  return await axiosRequest.delete(
    `v2/accounts/invitation/${inviteId}/revoke`, 
    { headers: getHeaders().common }
  )
}

export const removeMemberAccess = async (accountId: string,payload:object) => {
  const response = {
    status: false,
  };
  const data = await axiosRequest
    .post<any, AxiosResponse>(`v2/accounts/${accountId}/roles/updates`, payload, {
      headers: getHeaders().common,
    })
    .catch((r) => r);
  if (data?.data?.accepted_roles.length) {
    response.status = true;
  }
  return response;
}

export const getAllOrganizationFromCurrentWorkspace = async (workspaceId: string) => {
  try {
    return await axiosRequest.get<any, AxiosResponse<PageDTO<OrganizationDTO>>>(`v2/orgs?attributes=licensing_accounts&licensing_accounts=${workspaceId}`, {
      headers: getHeaders().common,
    });
  }
  catch (e) {
    console.log("error",e);
  }
};

export const getAllPropertiesFromCurrentWorkspace = async (orgIds: any) => {
  try {
    return await axiosRequest.post<any, AxiosResponse<PageDTO<any>>>(`v2/orgs/ids/properties`, {
     ids:orgIds
    },
    {
      headers: getHeaders().common,
    }
    );
  }
  catch (e) {
    console.log("error",e);
  }
};

export const getAllPropertiesOfOrganization = async (orgId: any) => {
  try {
    const data = await axiosRequest.get<any, AxiosResponse<PageDTO<PropertyDTO>>>(`/v2/orgs/${orgId}/properties`,
    {
      headers: getHeaders().common,
    }
    );
    if(data.status === 200){
      return data.data.content;
    }else{
      return false;
    }
  }
  catch (e) {
    console.log("error",e);
  }
};

export const getAllPropertiesByOrgIds = async (orgIds: any,params = {size:9999}) => {
  try {
    return await axiosRequest.post<any, AxiosResponse<PageDTO<PropertyDTO>>>(`v2/orgs/ids/properties?size=${params.size}`, {
     ids:orgIds
    },
    {
      headers: getHeaders().common,
    }
    );
  }
  catch (e) {
    console.log("error",e);
  }
};

export const getOrgsAndProperties = async (currentWorkspaceId: string) => {
  let getAllOrganizationsResponse: any = await getAllOrganizationFromCurrentWorkspace(currentWorkspaceId);
  let getAllPropertiesResponse: any = [];
  const orgIds = arrayofIds(getAllOrganizationsResponse.data.content);
  if (getAllOrganizationsResponse?.status === 200) {
    getAllPropertiesResponse = await Promise.all(orgIds.map((org: any) => getAllPropertiesOfOrganization(org)))
  }
  return {
    orgIds, getAllPropertiesResponse
  };
}

export const triggerUserDeletionFlow = async (payload: any) => {
  try {
    return await axiosRequest.post<any, AxiosResponse>(`v2/notifications/jira/issue`, payload,
    {
      headers: getHeaders().common,
    }
    );
  }
  catch (e) {
    console.log("error",e);
  }
}
